import { MOCK_CLIENT_PROFILE } from '../../_mock-data'

// Every shade `bandFields` can return. `bandFields`' return type is pinned to
// this union, so adding a band without adding it here is a compile error
// rather than a legend that explains fewer colours than the table paints.
export type EloBandColor = (typeof ELO_SHADE_RAMP)[number]['color']

// The legend, as the ramp it actually is. `bandFields` shades symmetrically
// around the client's rating — `--cd-green-soft` ("in band") lands on BOTH
// sides of centre — so the ramp lists it twice rather than pretending the
// mapping is monotonic easiest-to-hardest. Colours repeat; that is the point.
export const ELO_SHADE_RAMP = [
  { color: 'var(--cd-blue-deep)', label: 'much easier' },
  { color: 'var(--cd-blue)', label: 'easier' },
  { color: 'var(--cd-green-soft)', label: 'in band' },
  { color: 'var(--cd-green)', label: 'optimal' },
  { color: 'var(--cd-green-soft)', label: 'in band' },
  { color: 'var(--cd-amber)', label: 'harder' },
  { color: 'var(--cd-red)', label: 'much harder' },
] as const satisfies readonly { color: string; label: string }[]

/**
 * The rating the shading is relative to. Persisted mode reads the engagement's
 * real numbers, which arrive asynchronously and can legitimately be absent
 * (null context => no shading); mock mode uses the fixture profile. Shared so
 * the ELO cells, their tooltips, and the header legend can never quote three
 * different bands for one column of colours.
 */
export interface EloContext {
  rating: number
  band: number
  lower: number
  upper: number
  /** Who the shading is relative to, for tooltip copy. */
  subject: string
}

export function eloContext(source: {
  persisted?: boolean
  clientElo?: number | null
  optimalEloBand?: number | null
}): EloContext | null {
  const rating = source.persisted
    ? source.clientElo
    : MOCK_CLIENT_PROFILE.elo.rating
  const band = source.persisted
    ? source.optimalEloBand
    : MOCK_CLIENT_PROFILE.elo.optimalBand
  if (rating == null || band == null) return null
  return {
    rating,
    band,
    lower: rating - band,
    upper: rating + band,
    subject: source.persisted ? 'the client' : 'Jordan',
  }
}

export function bandFields(
  elo: number,
  rating: number,
  band: number,
): { color: EloBandColor; phrase: string; delta: number } {
  const delta = elo - rating
  const distance = Math.abs(delta)

  if (distance <= 50) {
    return {
      color: 'var(--cd-green)',
      phrase: 'dead-center of the optimal band',
      delta,
    }
  }
  if (elo >= rating - band && elo <= rating + band) {
    return {
      color: 'var(--cd-green-soft)',
      phrase: 'inside the optimal band',
      delta,
    }
  }
  if (elo < rating - band) {
    return distance <= 250
      ? {
          color: 'var(--cd-blue)',
          phrase: 'a bit easier than the optimal band',
          delta,
        }
      : {
          color: 'var(--cd-blue-deep)',
          phrase: 'well below the optimal band',
          delta,
        }
  }
  return distance <= 250
    ? {
        color: 'var(--cd-amber)',
        phrase: 'a bit harder than the optimal band',
        delta,
      }
    : {
        color: 'var(--cd-red)',
        phrase: 'far above the optimal band',
        delta,
      }
}
